/**
 * Removes personally identifiable information from values destined for Google Analytics 4.
 *
 * Google's policies forbid sending data that could identify an individual, and a free-text search box is a common
 * accidental source: users paste their own email address, a reference number, or a phone number into it.
 *
 * Pattern matching cannot detect a person's name or a free-form description of their circumstances. If the search box
 * is ever used to look up people or cases, treat this as a safety net and send a category of search instead.
 */

const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const URL_PATTERN = /\bhttps?:\/\/\S+/gi;
const JSON_WEB_TOKEN_PATTERN = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+/g;
const API_KEY_PATTERN = /\b(?:sk|pk|api|apikey|key|token|secret|bearer)[_-][A-Za-z0-9_-]{16,}\b/gi;
const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const IBAN_PATTERN = /\b[A-Z]{2}\d{2}(?:[ ]?[A-Z0-9]){11,30}\b/g;
const NATIONAL_IDENTIFIER_PATTERN = /\b\d{3}-\d{2}-\d{4}\b/g;
const VIN_PATTERN = /\b[A-HJ-NPR-Z0-9]{17}\b/gi;

/**
 * The last eight characters of a VIN, the form people actually type when searching. Requires the trailing six to be
 * digits, which is the usual shape of the sequential serial number. Without that constraint the rule would match any
 * eight-character token in the VIN alphabet — including ordinary English words such as `warranty`, whose letters all
 * happen to avoid the excluded I, O, and Q.
 */
const PARTIAL_VIN_PATTERN = /\b[A-HJ-NPR-Z0-9]{2}\d{6}\b/gi;
const INTERNATIONAL_PHONE_PATTERN = /\+\d[\d ().-]{7,16}\d/g;
const NORTH_AMERICAN_PHONE_PATTERN = /(?:\b1[ .-])?\(?\b\d{3}\b\)?[ .-]\d{3}[ .-]\d{4}\b/g;
const IPV4_ADDRESS_PATTERN = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
const IPV6_ADDRESS_PATTERN = /\b(?:[0-9a-f]{1,4}:){2,7}[0-9a-f]{1,4}\b/gi;
const DATE_PATTERN =
    /\b(?:(?:19|20)\d{2}[-/.](?:0?[1-9]|1[0-2])[-/.](?:0?[1-9]|[12]\d|3[01])|(?:0?[1-9]|1[0-2])[-/.](?:0?[1-9]|[12]\d|3[01])[-/.](?:19|20)\d{2})\b/g;
const POSTAL_CODE_PATTERN = /\b(?:[A-Z]{1,2}\d[A-Z\d]?[ ]?\d[A-Z]{2}|\d{5}-\d{4})\b/gi;
const STREET_ADDRESS_PATTERN =
    /\b\d{1,5}[ ](?:[A-Za-z0-9'.-]+[ ]){0,3}(?:street|st|avenue|ave|road|rd|boulevard|blvd|drive|dr|lane|ln|court|ct|circle|cir|way|place|pl|terrace|highway|hwy|parkway|pkwy|suite|ste|apartment|apt|unit)\b\.?/gi;

const KEYED_SECRET_PATTERN =
    /\b(?:ssn|social security(?: number)?|password|passwd|pwd|passcode|pin|dob|date of birth|driver'?s? licen[cs]e|licen[cs]e(?: number| no)?|passport(?: number| no)?|account(?: number| no)|card(?: number| no)|policy(?: number| no)|customer(?: id| number))\b[ ]*[:=#]?[ ]*\S+/gi;

const LONG_NUMBER_PATTERN = /\b\d{7,}\b/g;

type RedactionRule = {
    readonly pattern: RegExp;
    readonly replacement: string;
};

/**
 * Applied in order, and the order matters: a rule that matches a long structured value must run before one that
 * matches a short or loose value, or the earlier rule consumes characters the later rule needed. Email runs before VIN
 * because a seventeen-character local part matches the VIN alphabet, which would otherwise yield
 * `(redacted:vin)@example.com` and leak the domain.
 */
const redactionRules: readonly RedactionRule[] = [
    { pattern: EMAIL_PATTERN, replacement: '(redacted:email)' },
    { pattern: URL_PATTERN, replacement: '(redacted:url)' },
    { pattern: JSON_WEB_TOKEN_PATTERN, replacement: '(redacted:credential)' },
    { pattern: API_KEY_PATTERN, replacement: '(redacted:credential)' },
    { pattern: KEYED_SECRET_PATTERN, replacement: '(redacted:keyed-secret)' },
    { pattern: UUID_PATTERN, replacement: '(redacted:uuid)' },
    { pattern: IBAN_PATTERN, replacement: '(redacted:iban)' },
    { pattern: NATIONAL_IDENTIFIER_PATTERN, replacement: '(redacted:national-id)' },
    { pattern: VIN_PATTERN, replacement: '(redacted:vin)' },
    { pattern: PARTIAL_VIN_PATTERN, replacement: '(redacted:vin)' },
    { pattern: INTERNATIONAL_PHONE_PATTERN, replacement: '(redacted:phone)' },
    { pattern: NORTH_AMERICAN_PHONE_PATTERN, replacement: '(redacted:phone)' },
    { pattern: IPV6_ADDRESS_PATTERN, replacement: '(redacted:ip-address)' },
    { pattern: IPV4_ADDRESS_PATTERN, replacement: '(redacted:ip-address)' },
    { pattern: DATE_PATTERN, replacement: '(redacted:date-of-birth)' },
    { pattern: STREET_ADDRESS_PATTERN, replacement: '(redacted:street-address)' },
    { pattern: POSTAL_CODE_PATTERN, replacement: '(redacted:postal-code)' },
    { pattern: LONG_NUMBER_PATTERN, replacement: '(redacted:long-number)' },
];

/**
 * Anti-evasion hygiene, not a transformation: folds look-alike characters, strips control and zero-width characters
 * that could be interleaved inside an identifier, and collapses whitespace. Casing and length are left alone.
 */
function normaliseForMatching(value: string): string {
    return value
        .normalize('NFKC')
        .replace(/[\u0000-\u001F\u007F\u200B-\u200D\uFEFF]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Replaces every recognised identifier with a labelled placeholder, leaving the surrounding text intact. Use this on
 * the free-text search term and on any other string parameter before it reaches an analytics payload.
 */
export function redactValue(value: string): string {
    return redactionRules.reduce(
        (currentValue, rule) => currentValue.replace(rule.pattern, rule.replacement),
        normaliseForMatching(value)
    );
}
